import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { Notification } from '../models/notification.model';

@Injectable({
    providedIn: 'root'
})
export class SignalRService {
    private hubConnection: HubConnection | null = null;
    private connectionState = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
    private notificationReceived = new Subject<Notification>();
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectInterval = 5000; // 5 seconds

    public connectionState$ = this.connectionState.asObservable();
    public notificationReceived$ = this.notificationReceived.asObservable();

    constructor(private authService: AuthService) {
        // Auto-connect when user is authenticated
        this.authService.isAuthenticated$.subscribe(isAuthenticated => {
            if (isAuthenticated) {
                this.startConnection().then();
            } else {
                this.stopConnection().then();
            }
        });
    }

    /**
     * Start SignalR connection with authentication
     */
    public async startConnection(): Promise<void> {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            return;
        }

        if (!this.authService.getAccessToken()) {
            console.warn('No authentication token found. Cannot establish SignalR connection.');
            return;
        }

        try {
            this.hubConnection = new HubConnectionBuilder()
                .withUrl(`${environment.signalRHubUrl}/notifications`, {
                    // Asked for again on every (re)connect, so a reconnect after the access token ran out gets a fresh one.
                    accessTokenFactory: () => this.authService.getValidAccessToken(),
                    withCredentials: false
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: retryContext => {
                        if (retryContext.previousRetryCount < 3) {
                            return 1000 * Math.pow(2, retryContext.previousRetryCount);
                        }
                        return 30000; // 30 seconds for subsequent attempts
                    }
                })
                .configureLogging(LogLevel.Information)
                .build();

            // Set up event handlers
            this.setupEventHandlers();

            // Start the connection
            await this.hubConnection.start();
            console.log('SignalR connection established successfully');

            this.connectionState.next(this.hubConnection.state);
            this.reconnectAttempts = 0;

            // Join user-specific group
            await this.joinUserGroup();

        } catch (error) {
            console.error('Error starting SignalR connection:', error);
            this.handleConnectionError();
        }
    }

    /**
     * Stop SignalR connection
     */
    public async stopConnection(): Promise<void> {
        if (this.hubConnection) {
            try {
                await this.hubConnection.stop();
                console.log('SignalR connection stopped');
            } catch (error) {
                console.error('Error stopping SignalR connection:', error);
            }
            this.connectionState.next(HubConnectionState.Disconnected);
        }
    }

    /**
     * Setup event handlers for SignalR connection
     */
    private setupEventHandlers(): void {
        if (!this.hubConnection) return;

        // Connection state change handlers
        this.hubConnection.onclose((error) => {
            console.log('SignalR connection closed', error);
            this.connectionState.next(HubConnectionState.Disconnected);
            this.handleConnectionError();
        });

        this.hubConnection.onreconnecting((error) => {
            console.log('SignalR connection reconnecting', error);
            this.connectionState.next(HubConnectionState.Reconnecting);
        });

        this.hubConnection.onreconnected((connectionId) => {
            console.log('SignalR connection reconnected', connectionId);
            this.connectionState.next(HubConnectionState.Connected);
            this.reconnectAttempts = 0;
            this.joinUserGroup().then();
        });

        // Notification handlers
        this.hubConnection.on('ReceiveMessage', (notification: Notification) => {
            console.log('Notification received:', notification);
            this.notificationReceived.next(notification);
        });
    }

    /**
     * Join user-specific group for targeted notifications
     */
    private async joinUserGroup(): Promise<void> {
        if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
            return;
        }

        try {
            const userId = this.getCurrentUserId();
            const tenantId = this.getCurrentTenantId();

            if (userId && tenantId) {
                await this.hubConnection.invoke('JoinUserGroup', userId, tenantId);
                console.log(`Joined user group for user: ${userId}, tenant: ${tenantId}`);
            }
        } catch (error) {
            console.error('Error joining user group:', error);
        }
    }

    /**
     * Handle connection errors and implement retry logic
     */
    private handleConnectionError(): void {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

            setTimeout(() => {
                this.startConnection().then();
            }, this.reconnectInterval * this.reconnectAttempts);
        } else {
            console.error('Max reconnection attempts reached. Please refresh the page.');
        }
    }

    /**
     * Utility methods
     */
    private getCurrentUserId(): string {
        const currentUser = this.authService.getCurrentUser();
        return currentUser?.id || '';
    }

    private getCurrentTenantId(): string {
        // Assuming tenant info is stored in localStorage or can be retrieved from a service
        return localStorage.getItem('tenantId') || '';
    }
}
